import { shouldEnableFormTracking } from '../../../config/SiteConfigManager';
import { logger } from '../../../utils/core/logger';
import type {
    FormDetectionContext,
    FormDetectionManagerInterface,
    FormDetectionStrategy,
    FormElement,
} from '../types';

// Strategy: Unbounce Form Detection (hook-gated)
//
// Unbounce landing-page forms post submissions to Unbounce's Form Submission
// Gateway ("/fsg") via XHR, then (per the page's routingStrategy) usually
// redirect to a confirmation/next-step page. The form fires a native `submit`
// event even when the submission is rejected client-side (validation,
// reCAPTCHA) or server-side, so tracking on the submit event records phantom
// conversions in Rose with no matching Unbounce/HubSpot lead (IX-3127).
//
// Unbounce exposes its own lifecycle hooks at `window.ub.hooks`:
//   - beforeFormSubmit: fires after validation passes, before the POST
//   - afterFormSubmit:  fires after a SUCCESSFUL submission, before any redirect
//
// We register an `afterFormSubmit` callback. It is Unbounce's own "lead
// recorded" signal: authoritative (a rejected submit never reaches it),
// synchronous, and fired before the redirect, so it survives the navigation
// that defeats network/DOM observation.

const UNBOUNCE_GATEWAY_PATH = '/fsg';

type UnbounceFormSubmitHook = (formData?: unknown) => void;

interface UnbounceGlobal {
    hooks?: {
        afterFormSubmit?: UnbounceFormSubmitHook[];
        beforeFormSubmit?: UnbounceFormSubmitHook[];
    };
}

function getUnbounceHooks(): UnbounceFormSubmitHook[] | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const ub = (window as unknown as { ub?: UnbounceGlobal }).ub;
    const hook = ub?.hooks?.afterFormSubmit;
    return Array.isArray(hook) ? hook : null;
}

export class UnbounceFormStrategy implements FormDetectionStrategy {
    name = 'unbounce_form';
    private manager: FormDetectionManagerInterface | null = null;
    // `afterFormSubmit` is a page-global hook; register exactly once even if the
    // page has multiple Unbounce forms.
    private registeredHook: UnbounceFormSubmitHook | null = null;

    setManager(manager: FormDetectionManagerInterface): void {
        this.manager = manager;
    }

    canHandle(form: FormElement, context: FormDetectionContext): boolean {
        // Claim ONLY Unbounce `/fsg` forms whose hook API we can observe. If the
        // hook API is missing, return false so CTAPageFormStrategy still tracks
        // the form (degraded) rather than us claiming it and tracking nothing.
        if (this.formEndpointPath(form) !== UNBOUNCE_GATEWAY_PATH) {
            return false;
        }
        if (getUnbounceHooks() === null) {
            logger.debug('🟣 UnbounceFormStrategy: /fsg form but ub.hooks unavailable, deferring', {
                formId: form.id,
            });
            return false;
        }

        const shouldTrack = shouldEnableFormTracking(context.domain);
        logger.debug('🟣 UnbounceFormStrategy.canHandle', {
            formId: form.id,
            formAction: form.action,
            shouldTrack,
        });
        return shouldTrack;
    }

    private formEndpointPath(form: FormElement): string {
        if (!form.action) {
            return '';
        }
        try {
            return new URL(form.action, window.location.href).pathname;
        } catch {
            return '';
        }
    }

    apply(form: FormElement, context: FormDetectionContext): void {
        if (this.registeredHook) {
            return;
        }
        const hooks = getUnbounceHooks();
        if (!hooks) {
            return;
        }

        const fieldCount = form.querySelectorAll('input, select, textarea').length;

        const onAfterFormSubmit: UnbounceFormSubmitHook = () => {
            // Unbounce only invokes afterFormSubmit on a successful submission.
            if (this.manager && !this.manager.shouldTrackSubmission(form)) {
                return;
            }

            logger.debug('🟣 Unbounce afterFormSubmit fired, tracking conversion', {
                formId: form.id,
            });

            context.onFormSubmit({
                formId: form.id || 'unbounce_form',
                formClass: form.className || '',
                pageUrl: context.currentUrl,
                formAction: form.action || '',
                formMethod: form.method || 'POST',
                fieldCount,
                detectionMethod: this.name,
                provider: 'unbounce',
                status: 'success',
                indicators: ['unbounce_after_form_submit'],
            });
        };

        hooks.push(onAfterFormSubmit);
        this.registeredHook = onAfterFormSubmit;

        logger.debug('🟣 UnbounceFormStrategy: registered afterFormSubmit hook', {
            formId: form.id,
        });
    }

    cleanup(): void {
        if (!this.registeredHook) {
            return;
        }
        const hooks = getUnbounceHooks();
        if (hooks) {
            const idx = hooks.indexOf(this.registeredHook);
            if (idx !== -1) {
                hooks.splice(idx, 1);
            }
        }
        this.registeredHook = null;
        logger.debug('🟣 UnbounceFormStrategy: cleaned up');
    }
}
